import { useMemo } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  useMyServices,
  serviceLabel,
  serviceProviderScope,
} from '@/api/services';
import { SERVICE_CATEGORIES, categoryOfScope, serviceRoute, isScopeCategoryEnabled } from './categories';
import { groupCDNServices } from './cdn-service-grouping';

export interface ServicesNavLeaf {
  /** Service id (CDN: the umbrella's canonical service id). */
  id: string;
  label: string;
  /** Console deep-link; services without a wired console fall back to the dashboard category filter. */
  route: string;
}

export interface ServicesNavCategory {
  key: string;
  title: string;
  icon: LucideIcon;
  services: ServicesNavLeaf[];
}

/**
 * Sidebar tree of the org's PROVISIONED services, grouped by catalog category —
 * each leaf deep-links straight into its service console. Mirrors the
 * ServicesDashboard grouping: CDN provider rows collapse into one Multi-CDN
 * umbrella per org (leaf = the canonical/vEdge service), so the sidebar never
 * lists vendor sub-services. Categories with no provisioned service are
 * omitted (catalog browsing lives on /services); scopes the taxonomy doesn't
 * know ("other") are dashboard-only too.
 */
export function useServicesNavTree(): ServicesNavCategory[] {
  const { data: services = [] } = useMyServices();
  return useMemo(() => {
    const byCategory = new Map<string, ServicesNavLeaf[]>();
    const push = (key: string, leaf: ServicesNavLeaf) => {
      const list = byCategory.get(key);
      if (list) list.push(leaf);
      else byCategory.set(key, [leaf]);
    };

    for (const s of services) {
      const scope = serviceProviderScope(s);
      const key = categoryOfScope(scope);
      if (key === 'cdn' || key === 'other') continue; // CDN grouped below; unknown scopes stay dashboard-only
      if (!isScopeCategoryEnabled(scope)) continue; // brand allowlist: hide disabled categories
      push(key, {
        id: s.id,
        label: serviceLabel(s),
        route: serviceRoute(scope, s.id) ?? `/services?category=${key}`,
      });
    }

    // One umbrella leaf per org-group of CDN provider services.
    for (const g of groupCDNServices(services)) {
      const c = g.canonical;
      push('cdn', {
        id: c.id,
        label: serviceLabel(c),
        route: serviceRoute(serviceProviderScope(c), c.id) ?? `/services/cdn/${c.id}`,
      });
    }

    // Keep the catalog's category order; sort services alphabetically within.
    return SERVICE_CATEGORIES.filter((c) => byCategory.has(c.key)).map((c) => ({
      key: c.key,
      title: c.title,
      icon: c.icon,
      services: byCategory
        .get(c.key)!
        .sort((a, b) => a.label.localeCompare(b.label)),
    }));
  }, [services]);
}
